/**
 * Terms of service.
 *
 * The clause that matters most here is the advisory one: FlightSlots reports
 * findings and never clears a flight, and the terms have to say that in the
 * same words the product does.
 */

import { Link } from 'react-router-dom';
import { DocSection, PublicPage } from './PublicPage.js';

export function Terms(): JSX.Element {
  return (
    <PublicPage
      title="Terms of service"
      updated="29 August 2026"
      lede="The agreement for using FlightSlots. Plain wording on purpose — you should not need a lawyer to find out what you agreed to."
    >
      <DocSection title="What FlightSlots is">
        <p>
          A scheduling and record-keeping tool for flight training. You get an account, your school's
          records live in it, and you pay for it monthly or by arrangement.
        </p>
      </DocSection>

      <DocSection id="advisory" title="It is advisory, and this is the important part">
        <p>
          <strong>FlightSlots never says a flight is legal, safe or approved.</strong> It reports
          findings, each with the fact it came from and the rule it was measured against. No screen
          in the product says &ldquo;cleared&rdquo;.
        </p>
        <p>
          The pilot in command decides whether a flight is conducted, and the school and its
          instructors remain responsible for airworthiness, currency, endorsements, duty and
          syllabus compliance. A finding we did not raise is not a defence, and a finding we did
          raise is not an authorisation. Regulations change, records can be entered wrongly, and an
          aircraft's real condition is determined by looking at it.
        </p>
        <p>
          Duty limits in FlightSlots are the policy your school configures. Part 61 and Part 141
          instruction carry no federal duty limit, and we label these findings as school policy
          precisely so nobody mistakes them for a federal one.
        </p>
      </DocSection>

      <DocSection title="Your account">
        <ul>
          <li>Give accurate information, and keep your password to yourself.</li>
          <li>You are responsible for what happens under your account, so tell us promptly if you think someone else is in it.</li>
          <li>Do not use FlightSlots to break the law, to harass anyone, or to store records you have no right to hold.</li>
          <li>Do not attack the service, scrape it wholesale, or try to reach another school's records. Reporting a flaw responsibly is welcome and covered on the <Link to="/security">security page</Link>.</li>
        </ul>
      </DocSection>

      <DocSection title="Your records are yours">
        <p>
          You keep every right you have in the records you put into FlightSlots. We store and process
          them to run the service for you, and for nothing else — we do not sell them, mine them, or
          train models on them.
        </p>
        <p>
          You can export training records, stage records and invoices at any time, and leaving does
          not require our cooperation. If you cancel, we keep your data for thirty days so an
          accident is recoverable, then delete it; backups age out on the same thirty-day cycle
          described in the <Link to="/privacy">privacy statement</Link>.
        </p>
      </DocSection>

      <DocSection title="Paying">
        <p>
          The freelance plan is a flat monthly fee, billed in advance and cancellable at any time
          with effect from the end of the period you have paid for. School and program pricing is
          agreed in writing before it starts. If a price changes we tell you at least thirty days
          before it applies to you, and you can cancel instead.
        </p>
        <p>
          We do not bill for a period you did not use because the service was unavailable — tell us
          and we will credit it.
        </p>
      </DocSection>

      <DocSection title="Availability">
        <p>
          We aim to keep FlightSlots running and we publish live <Link to="/status">status</Link>,
          but we do not offer a contractual uptime guarantee at these prices, and it would be
          dishonest to print one. Planned maintenance is short, and as described on the security
          page a deploy is a brief restart.
        </p>
      </DocSection>

      <DocSection title="Ending it">
        <p>
          You can stop using FlightSlots whenever you like, and export your records on the way out.
          We can suspend or end an account that is being used to attack the service or to break the
          law, or where fees have gone unpaid after we have asked. Except where an account is being
          used to cause harm, we give notice and a chance to export first.
        </p>
      </DocSection>

      <DocSection title="Liability">
        <p>
          FlightSlots is provided as it is. To the extent the law allows, we are not liable for lost
          profits, lost business, or for decisions made in reliance on a finding — see the advisory
          clause above, which is the whole basis on which this product is offered. Where liability
          cannot be excluded, it is limited to the fees you paid us in the twelve months before the
          claim.
        </p>
        <p>
          Nothing here limits liability for death or personal injury caused by negligence, for fraud,
          or for anything else that cannot lawfully be limited.
        </p>
      </DocSection>

      <DocSection title="Changes to these terms">
        <p>
          If we change them materially we will tell you in the application before the change takes
          effect, not by silently editing this page. Continuing to use FlightSlots after that is
          acceptance; if you would rather not, cancel and export.
        </p>
      </DocSection>

      <DocSection title="Questions">
        <p>
          Ask through the <Link to="/contact">contact form</Link> and a person will answer.
        </p>
      </DocSection>
    </PublicPage>
  );
}
